import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Editor from '@monaco-editor/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Play, Sparkles, Send, Loader2, Users, Activity, Code2, Eye, Maximize2, X, ChevronRight, ChevronLeft, Package, AlertCircle, RefreshCw, Shield, Target, TestTube, CheckCircle, Brain, FileText, MessageSquare, ExternalLink, Paperclip, Image, Upload } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  ATC_REMEDIATION_STAGE,
  deployAbapCode,
  formatApiError,
  mergeChainOfThoughtWithinByteLimit,
  startAbapAtcRemediation,
  waitForAbapAtcRemediation,
} from '@/lib/abapDeployApi';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { processSecurityMessage, pickActiveCodeGeneration, isSecurityTicketType, isSecurityAgentRunning, isSecurityAgentComplete, isSecurityAgentFailed, resolveSecurityAgentMessage } from '@/lib/ticketCodeHelpers';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { TransportLifecycleTracker } from '@/components/TransportLifecycleTracker';
import { PlatformConnectionStatus } from '@/components/PlatformConnectionStatus';
import { usePlatformConnectionMode } from '@/lib/usePlatformConnectionMode';

const API = INCIDENTS_API;

/** Normalize a ticket type string to lowercase for comparisons. */
function normalizeTicketType(type) {
  return (type || '').toLowerCase().trim();
}

const ABAP_WORKFLOW_STAGE = {
  GENERATING: 'generating',
  VALIDATING: 'validating',
  VALIDATED: 'validated',
  VALIDATION_FAILED: 'validation_failed',
  DEPLOYING: 'deploying',
  VALIDATED_AND_DEPLOYED: 'validated_and_deployed',
  ATC_REMEDIATING: ATC_REMEDIATION_STAGE.REMEDIATING,
  ATC_CLEAN: ATC_REMEDIATION_STAGE.CLEAN,
  ATC_PARTIAL: ATC_REMEDIATION_STAGE.PARTIAL,
};

const ABAP_WORKFLOW_STAGE_LABELS = {
  [ABAP_WORKFLOW_STAGE.GENERATING]: 'Generating',
  [ABAP_WORKFLOW_STAGE.VALIDATING]: 'Validating',
  [ABAP_WORKFLOW_STAGE.VALIDATED]: 'Validated',
  [ABAP_WORKFLOW_STAGE.VALIDATION_FAILED]: 'Validation Failed',
  [ABAP_WORKFLOW_STAGE.DEPLOYING]: 'Deploying',
  [ABAP_WORKFLOW_STAGE.VALIDATED_AND_DEPLOYED]: 'Validated and deployed',
  [ABAP_WORKFLOW_STAGE.ATC_REMEDIATING]: 'ATC remediating',
  [ABAP_WORKFLOW_STAGE.ATC_CLEAN]: 'ATC clean (P1)',
  [ABAP_WORKFLOW_STAGE.ATC_PARTIAL]: 'ATC partial',
};

function formatDeployMessageForCot(result) {
  return (result?.message || '').trim() || 'Deploy completed.';
}

function isValidationPassedStatus(validationStatus) {
  return ['pass', 'partial', 'skipped', 'unknown'].includes(validationStatus);
}

function isDeployFailed(deployStatus, deployResponse) {
  if (deployStatus === 'failed') return true;
  if (deployResponse && deployResponse.success === false) return true;
  return false;
}

function isAtcPartial(deployStatus, workflowStage) {
  if (deployStatus === 'partial') return true;
  if (workflowStage === ABAP_WORKFLOW_STAGE.ATC_PARTIAL) return true;
  return false;
}

function isDeploySucceeded(deployStatus, deployResponse, workflowStage) {
  // atc_partial is incomplete remediation — not success (see isAtcPartial).
  if (isAtcPartial(deployStatus, workflowStage)) return false;
  if (deployStatus === 'deployed') return true;
  if (workflowStage === ABAP_WORKFLOW_STAGE.VALIDATED_AND_DEPLOYED) return true;
  if (workflowStage === ABAP_WORKFLOW_STAGE.ATC_CLEAN) return true;
  if (deployResponse && deployResponse.success === true) return true;
  return false;
}

function isStaleDeployingInDb(workflowStage, deployResponse, deployInFlight) {
  return (
    (workflowStage === ABAP_WORKFLOW_STAGE.DEPLOYING
      || workflowStage === ABAP_WORKFLOW_STAGE.ATC_REMEDIATING)
    && !deployInFlight
    && !(deployResponse && deployResponse.success === true)
  );
}

function resolveValidationStatus(storedStatus, code, workflowStage) {
  if (storedStatus) return storedStatus;
  if (workflowStage === ABAP_WORKFLOW_STAGE.VALIDATION_FAILED) return 'fail';
  if (workflowStage === ABAP_WORKFLOW_STAGE.VALIDATED) return 'pass';
  if (workflowStage === ABAP_WORKFLOW_STAGE.VALIDATING) return null;
  return null;
}

function codeContentHash(code) {
  const trimmed = (code || '').trim();
  if (!trimmed) return '';
  return `${trimmed.length}:${trimmed.slice(0, 80)}`;
}

function deployStorageKey(ticketNumber) {
  return `halo-deploy-${ticketNumber}`;
}

function loadPersistedDeployState(ticketNumber) {
  try {
    const raw = sessionStorage.getItem(deployStorageKey(ticketNumber));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistDeployState(ticketNumber, state) {
  try { sessionStorage.setItem(deployStorageKey(ticketNumber), JSON.stringify(state)); } catch {}
}

function clearPersistedDeployState(ticketNumber) {
  try { sessionStorage.removeItem(deployStorageKey(ticketNumber)); } catch {}
}

async function persistTicketWorkflowStage(ticketNumber, fields) {
  await axios.put(`${API}/tickets/${ticketNumber}`, fields);
}

async function persistDeployOutcome(ticketNumber, { success, objectName, validationStatus }) {
  const restoreStage = (validationStatus === 'fail' || validationStatus === 'error')
    ? ABAP_WORKFLOW_STAGE.VALIDATION_FAILED
    : ABAP_WORKFLOW_STAGE.VALIDATED;
  await persistTicketWorkflowStage(ticketNumber, {
    abap_workflow_stage: success ? ABAP_WORKFLOW_STAGE.VALIDATED_AND_DEPLOYED : restoreStage,
    deployed_object_name: objectName || undefined,
  });
}

async function appendDeployResultToCot(ticketNumber, cotAppendText) {
  const { data: current } = await axios.get(`${API}/tickets/${ticketNumber}`);
  const chain_of_thought = mergeChainOfThoughtWithinByteLimit(
    current?.chain_of_thought || '',
    cotAppendText,
  );
  await persistTicketWorkflowStage(ticketNumber, { chain_of_thought });
}

function DeployActionButton({ deployStatus, deploying, hasCode, onClick, testId, className = '', workflowStage, canDeploy = true }) {
  const isDeployed = deployStatus === 'deployed' || workflowStage === ABAP_WORKFLOW_STAGE.VALIDATED_AND_DEPLOYED;
  const isDeploying = deployStatus === 'deploying' || deploying;
  return (
    <span title={permissionDeniedTitle(canDeploy, 'deploy ticket code')}>
      <Button
        size="sm"
        variant={isDeployed ? 'default' : 'outline'}
        className={isDeployed ? `bg-emerald-600 text-white hover:bg-emerald-600 border-emerald-600 cursor-default ${className}` : className}
        onClick={isDeployed ? undefined : onClick}
        disabled={!canDeploy || isDeploying || !hasCode || isDeployed}
        data-testid={testId}
      >
        {isDeploying ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" />Deploying…</>)
          : isDeployed ? (<><CheckCircle className="h-4 w-4 mr-1" />Deployed</>)
          : (<><Upload className="h-4 w-4 mr-1" />DEPLOY</>)}
      </Button>
    </span>
  );
}

function DeployAndAtcActionButton({ deploying, hasCode, onClick, testId, className = '', canDeploy = true }) {
  return (
    <span title={permissionDeniedTitle(canDeploy, 'deploy and run ATC')}>
      <Button
        size="sm"
        variant="outline"
        className={className}
        onClick={onClick}
        disabled={!canDeploy || deploying || !hasCode}
        data-testid={testId}
      >
        {deploying ? (
          <><Loader2 className="h-4 w-4 animate-spin mr-1" />Remediating ATC…</>
        ) : (
          <><TestTube className="h-4 w-4 mr-1" />Deploy &amp; Run ATC</>
        )}
      </Button>
    </span>
  );
}

// Utility function to extract content between triple backticks
function extractCodeFromBackticks(content) {
  if (!content) return content;
  
  // Match content between triple backticks (```)
  const codeBlockRegex = /```[\s\S]*?```/g;
  const matches = content.match(codeBlockRegex);
  
  if (matches && matches.length > 0) {
    // Extract content from all code blocks and join them
    return matches
      .map(match => {
        // Remove the triple backticks from start and end
        return match.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
      })
      .join('\n\n');
  }
  
  // If no triple backticks found, return original content
  return content;
}

// Utility function to extract screenshot URLs from ticket description
// Only extracts URLs matching the pattern: https://su53-screenshots.s3.us-west-2.amazonaws.com/*
function extractScreenshotUrls(description) {
  if (!description) return [];
  
  // Pattern to match screenshot URLs
  const screenshotUrlPattern = /https:\/\/su53-screenshots\.s3\.us-west-2\.amazonaws\.com\/[^\s\)]+/gi;
  const matches = description.match(screenshotUrlPattern);
  
  return matches || [];
}

// Utility function to check if ticket is of type Security (SapAuth agent)
// SapAuth agent is detected when customization_type is "Security"
function isSapAuthAgentTicket(ticket) {
  if (!ticket) return false;
  
  // customization_type is the main indicator for SapAuth agent
  const customizationType = (ticket.customization_type || '').toLowerCase();
  return customizationType === 'security';
}

// Typing dots component for thinking indicator
function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1">
      <span 
        className="w-2 h-2 bg-purple-500 rounded-full opacity-70"
        style={{
          animation: 'typingDot 1.4s infinite ease-in-out',
          animationDelay: '0ms'
        }}
      ></span>
      <span 
        className="w-2 h-2 bg-purple-500 rounded-full opacity-70"
        style={{
          animation: 'typingDot 1.4s infinite ease-in-out',
          animationDelay: '200ms'
        }}
      ></span>
      <span 
        className="w-2 h-2 bg-purple-500 rounded-full opacity-70"
        style={{
          animation: 'typingDot 1.4s infinite ease-in-out',
          animationDelay: '400ms'
        }}
      ></span>
      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

// Thinking indicator component - shows after latest message
function ThinkingIndicator({
  hasCode,
  validationStatus,
  isCodeReport,
  isSecurity,
  securityRunComplete,
  securityRunFailed,
  securityRunRunning,
  deployStatus,
  deployedObjectName,
  deployMessage,
  workflowStage,
  deployResponse,
  chainOfThought,
}) {
  const stage = workflowStage || null;
  const stageLabel = stage ? ABAP_WORKFLOW_STAGE_LABELS[stage] || stage : null;
  const deployFailed = isDeployFailed(deployStatus, deployResponse);
  const atcPartial = isAtcPartial(deployStatus, stage);
  const deploySucceeded = isDeploySucceeded(deployStatus, deployResponse, stage);
  const atcClean = stage === ABAP_WORKFLOW_STAGE.ATC_CLEAN;
  const PLATFORM_DISCONNECTED_BLOCKED = 'Platform disconnected - Blocked';
  const cotText = (chainOfThought || '').trim();
  const cotLower = cotText.toLowerCase();
  const platformBlocked =
    cotLower.includes('platform disconnect') ||
    cotLower.includes('disconnected mode') ||
    cotLower.includes('platform_disconnected') ||
    cotLower.includes('not available in disconnected mode');

  if (platformBlocked) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-amber-300 ring-offset-2">
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-sm text-amber-900">{PLATFORM_DISCONNECTED_BLOCKED}</div>
        </div>
      </div>
    );
  }

  if (isCodeReport && stage === ABAP_WORKFLOW_STAGE.GENERATING && !hasCode) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-indigo-300 ring-offset-2 animate-pulse">
          <Code2 className="h-4 w-4" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-indigo-50 to-violet-50 border-2 border-indigo-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-indigo-900">{stageLabel || 'Generating'}</div>
            <TypingDots />
          </div>
          <div className="text-xs text-indigo-800 mt-1">Generating ABAP code from ticket requirements…</div>
        </div>
      </div>
    );
  }

  if (isCodeReport && deployFailed) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-red-300 ring-offset-2">
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-red-900">Deploy / ATC failed</div>
            <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-400 font-medium">
              ✗ Error
            </Badge>
          </div>
          <div className="text-xs text-red-800 mt-1 whitespace-pre-wrap break-words">
            {deployMessage || deployResponse?.message || 'Deployment failed. See Chain of Thought for details.'}
          </div>
        </div>
      </div>
    );
  }

  if (isCodeReport && atcPartial) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-amber-300 ring-offset-2">
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-amber-900">ATC incomplete</div>
            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-400 font-medium">
              Partial
            </Badge>
          </div>
          <div className="text-xs text-amber-800 mt-1 whitespace-pre-wrap break-words">
            {deployMessage || deployResponse?.message ||
              'ATC remediation did not clear all P1/P2 findings. See Chain of Thought for details.'}
          </div>
        </div>
      </div>
    );
  }

  if (isCodeReport && deploySucceeded) {
    const successTitle = atcClean ? 'ATC clean' : 'Deploy succeeded';
    const successBadge = atcClean ? '✓ P1=P2=0' : '✓ Deployed';
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-teal-300 ring-offset-2">
          <CheckCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-teal-50 to-emerald-50 border-2 border-teal-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-teal-900">{successTitle}</div>
            <Badge variant="outline" className="text-xs bg-teal-100 text-teal-800 border-teal-400 font-medium">
              {successBadge}
            </Badge>
          </div>
          <div className="text-xs text-teal-800 mt-1 whitespace-pre-wrap break-words">
            {deployMessage || deployResponse?.message ||
              (atcClean
                ? 'ATC remediation complete — P1=P2=0 (P3 ignored).'
                : deployedObjectName
                  ? `${deployedObjectName} was written and activated in SAP`
                  : 'ABAP code was successfully deployed to SAP')}
          </div>
        </div>
      </div>
    );
  }

  if (
    isCodeReport &&
    !deployFailed &&
    !atcPartial &&
    (deployStatus === 'deploying' ||
      stage === ABAP_WORKFLOW_STAGE.ATC_REMEDIATING ||
      (stage === ABAP_WORKFLOW_STAGE.DEPLOYING && deployStatus !== 'failed'))
  ) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-blue-300 ring-offset-2 animate-pulse">
          <Upload className="h-4 w-4" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-blue-800">{stageLabel || 'Deploying'}</div>
            <TypingDots />
          </div>
          <div className="text-xs text-blue-700 mt-1">
            {stage === ABAP_WORKFLOW_STAGE.ATC_REMEDIATING
              ? (deployedObjectName
                ? `Deploying and remediating ATC for ${deployedObjectName}…`
                : 'Deploying and remediating ATC findings…')
              : (deployedObjectName
                ? `Uploading ${deployedObjectName} to SAP…`
                : 'Uploading ABAP source to SAP via WriteSource…')}
          </div>
        </div>
      </div>
    );
  }

  const validationFailed =
    stage === ABAP_WORKFLOW_STAGE.VALIDATION_FAILED ||
    validationStatus === 'fail' ||
    validationStatus === 'error';
  const validationPassed =
    stage === ABAP_WORKFLOW_STAGE.VALIDATED ||
    isValidationPassedStatus(validationStatus);

  if (isCodeReport && validationFailed) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-red-300 ring-offset-2">
          <span className="text-base">✗</span>
        </div>
        <div className="flex-1 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-red-800">Validation Failed</div>
            <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-400 font-medium">
              Syntax Errors
            </Badge>
          </div>
          <div className="text-xs text-red-700 mt-1">
            ABAP code is available for review — see Chain of Thought for validation details
          </div>
        </div>
      </div>
    );
  }

  if (isCodeReport && validationPassed) {
    const detail =
      validationStatus === 'pass'
        ? 'ABAP code generated and syntax validated successfully'
        : 'ABAP code generated (validation skipped or not available)';
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-green-300 ring-offset-2">
          <CheckCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-green-800">Validated</div>
            <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-400 font-medium">
              ✓ Ready to deploy
            </Badge>
          </div>
          <div className="text-xs text-green-700 mt-1">{detail}</div>
        </div>
      </div>
    );
  }

  if (isCodeReport && stage === ABAP_WORKFLOW_STAGE.VALIDATING && !validationStatus) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-yellow-300 ring-offset-2 animate-pulse">
          <span className="text-base">⚙</span>
        </div>
        <div className="flex-1 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-yellow-800">Validating</div>
            <TypingDots />
          </div>
          <div className="text-xs text-yellow-700 mt-1">Running ABAP syntax and quality checks…</div>
        </div>
      </div>
    );
  }

  if (isSecurity) {
    if (securityRunFailed) {
      return (
        <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-red-300 ring-offset-2">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="flex-1 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 rounded-lg p-3 shadow-lg">
            <div className="font-semibold text-sm text-red-900">Security request failed</div>
            <div className="text-xs text-red-800 mt-1">See Chain of Thought for details.</div>
          </div>
        </div>
      );
    }

    if (hasCode || securityRunComplete) {
      return (
        <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-green-300 ring-offset-2">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div className="flex-1 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-3 shadow-lg">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-sm text-green-800">Security analysis complete</div>
              <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-400 font-medium">
                ✓ Ready
              </Badge>
            </div>
            <div className="text-xs text-green-700 mt-1">Agent message is available on the right panel.</div>
          </div>
        </div>
      );
    }

    if (securityRunRunning) {
      return (
        <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-purple-300 ring-offset-2 animate-pulse">
            <Brain className="h-5 w-5" />
          </div>
          <div className="flex-1 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-2 border-purple-300 rounded-lg p-3 shadow-lg">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-sm text-purple-900">Analyzing security data</div>
              <TypingDots />
            </div>
            <div className="text-xs text-purple-700 font-medium">Security agent is querying SAP and preparing your summary…</div>
          </div>
        </div>
      );
    }

    return null;
  }

  if (hasCode) {
    if (isCodeReport) return null;
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-green-300 ring-offset-2">
          <CheckCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-green-800">Agent Completed</div>
            <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-400 font-medium">
              ✓ Ready
            </Badge>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 mt-2">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-lg ring-2 ring-purple-300 ring-offset-2 animate-pulse">
        <Brain className="h-5 w-5" />
      </div>
      <div className="flex-1 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-2 border-purple-300 rounded-lg p-3 shadow-lg">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm text-purple-900">Thinking</div>
          <TypingDots />
        </div>
        <div className="text-xs text-purple-700 font-medium">Agent is working on your task...</div>
      </div>
    </div>
  );
}

// Chain of Thought Component — receives value from parent (no independent fetch)
function ChainOfThought({ chainOfThought }) {
  if (!chainOfThought) {
    return null;
  }

  // Split by \n\n and display each section (latest first)
  const sections = chainOfThought.split('\n\n').filter(section => section.trim()).reverse();

  return (
    <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800 border border-purple-200 dark:border-purple-900/50 rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-purple-100 dark:bg-purple-950/60 rounded-lg">
            <Brain className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <div className="text-sm font-semibold text-purple-900 dark:text-purple-200">Chain of Thought</div>
            <div className="text-xs text-purple-600 dark:text-purple-400">AI Reasoning Process</div>
          </div>
        </div>
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar min-w-0">
        {sections.map((section, index) => (
          <div
            key={index}
            className="group text-sm bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-4 rounded-lg border border-purple-100/80 dark:border-purple-900/40 shadow-sm hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800 transition-all duration-200 leading-relaxed text-slate-700 dark:text-slate-300 min-w-0"
          >
            <div className="flex items-start gap-3 min-w-0">
              <div className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 shadow-sm"></div>
              <div className="flex-1 font-normal break-words whitespace-pre-wrap min-w-0 w-full" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                {section.trim()}
              </div>
            </div>
          </div>
        ))}
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(147, 51, 234, 0.3);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(147, 51, 234, 0.5);
          }
        `}</style>
      </div>
    </div>
  );
}

// Design Output View Component
function DesignOutputView({ ticketId, agentTasks }) {
  const designTask = agentTasks.find(t => t.agent_type === 'design');

  if (!designTask) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <Sparkles className="h-16 w-16 mx-auto mb-4 text-purple-300" />
          <p className="text-slate-600 mb-2">No design output yet</p>
          <p className="text-sm text-slate-500">Execute workflow to generate design document</p>
        </div>
      </div>
    );
  }

  const designOutput = designTask.output_data || {};
  const designDocument = typeof designOutput === 'string' ? designOutput : JSON.stringify(designOutput, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="bg-purple-50 border-b border-purple-200 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <span className="font-semibold text-purple-900">Design Agent Output</span>
          <Badge className="bg-purple-600 text-white text-xs">
            {designTask.status === 'completed' ? '✓ Completed' : designTask.status}
          </Badge>
        </div>
        {designTask.completed_at && (
          <span className="text-xs text-purple-700">
            {new Date(designTask.completed_at).toLocaleString()}
          </span>
        )}
      </div>
      
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          value={designDocument}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 12,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true
          }}
        />
      </div>

      {/* Quick Insights */}
      <div className="bg-slate-50 border-t border-slate-200 p-3">
        <div className="grid grid-cols-3 gap-3 text-xs">
          {designOutput.clean_core_compliant !== undefined && (
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>Clean Core: {designOutput.clean_core_compliant ? 'Yes' : 'No'}</span>
            </div>
          )}
          {designOutput.compliance_score !== undefined && (
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3 text-blue-600" />
              <span>Compliance: {designOutput.compliance_score}%</span>
            </div>
          )}
          {designOutput.data_model && (
            <div className="flex items-center gap-1">
              <Code2 className="h-3 w-3 text-purple-600" />
              <span>Data Model: Available</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Test Output View Component
function TestOutputView({ ticketId, agentTasks }) {
  const testTask = agentTasks.find(t => t.agent_type === 'test');

  if (!testTask) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <TestTube className="h-16 w-16 mx-auto mb-4 text-amber-300" />
          <p className="text-slate-600 mb-2">No test results yet</p>
          <p className="text-sm text-slate-500">Execute workflow to generate test cases</p>
        </div>
      </div>
    );
  }

  const testOutput = testTask.output_data || {};
  const testDocument = typeof testOutput === 'string' ? testOutput : JSON.stringify(testOutput, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="bg-amber-50 border-b border-amber-200 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TestTube className="h-5 w-5 text-amber-600" />
          <span className="font-semibold text-amber-900">Test Agent Output</span>
          <Badge className="bg-amber-600 text-white text-xs">
            {testTask.status === 'completed' ? '✓ Completed' : testTask.status}
          </Badge>
        </div>
        {testTask.completed_at && (
          <span className="text-xs text-amber-700">
            {new Date(testTask.completed_at).toLocaleString()}
          </span>
        )}
      </div>
      
      {/* Test Summary Cards */}
      <div className="bg-white border-b border-slate-200 p-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-green-50 rounded">
            <div className="text-2xl font-bold text-green-600">
              {testOutput.tests_passed || 0}
            </div>
            <div className="text-xs text-slate-600 mt-1">Passed</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded">
            <div className="text-2xl font-bold text-red-600">
              {testOutput.tests_failed || 0}
            </div>
            <div className="text-xs text-slate-600 mt-1">Failed</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded">
            <div className="text-2xl font-bold text-blue-600">
              {testOutput.total_tests || 0}
            </div>
            <div className="text-xs text-slate-600 mt-1">Total</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded">
            <div className="text-2xl font-bold text-purple-600">
              {testOutput.coverage_percentage || 0}%
            </div>
            <div className="text-xs text-slate-600 mt-1">Coverage</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          value={testDocument}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 12,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true
          }}
        />
      </div>
    </div>
  );
}

// DevOps Output View Component
function DevOpsOutputView({ ticketId, agentTasks }) {
  const devopsTask = agentTasks.find(t => t.agent_type === 'devops');

  if (!devopsTask) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <Package className="h-16 w-16 mx-auto mb-4 text-orange-300" />
          <p className="text-slate-600 mb-2">No DevOps output yet</p>
          <p className="text-sm text-slate-500">Execute workflow to generate deployment artifacts</p>
        </div>
      </div>
    );
  }

  const devopsOutput = devopsTask.output_data || {};
  const devopsDocument = typeof devopsOutput === 'string' ? devopsOutput : JSON.stringify(devopsOutput, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="bg-orange-50 border-b border-orange-200 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-orange-600" />
          <span className="font-semibold text-orange-900">DevOps Agent Output</span>
          <Badge className="bg-orange-600 text-white text-xs">
            {devopsTask.status === 'completed' ? '✓ Completed' : devopsTask.status}
          </Badge>
        </div>
        {devopsTask.completed_at && (
          <span className="text-xs text-orange-700">
            {new Date(devopsTask.completed_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Transport Info */}
      {devopsOutput.transport_number && (
        <div className="bg-white border-b border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-600 mb-1">Transport Request</div>
              <div className="text-lg font-bold text-orange-600 font-mono">
                {devopsOutput.transport_number}
              </div>
            </div>
            <Badge className="bg-orange-100 text-orange-700">Ready for QA</Badge>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          value={devopsDocument}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 12,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true
          }}
        />
      </div>

      {/* Deployment Artifacts */}
      {devopsOutput.artifacts && devopsOutput.artifacts.length > 0 && (
        <div className="bg-slate-50 border-t border-slate-200 p-3">
          <div className="text-xs font-semibold text-slate-700 mb-2">
            Deployment Artifacts ({devopsOutput.artifacts.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {devopsOutput.artifacts.map((artifact, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {artifact}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Intelligent Fiori Preview Component - Generates ticket-specific UI
function FioriPreview({ code, ticket, isVisible }) {
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    const intelligentFioriUI = generateIntelligentFioriUI(ticket, code);
    setPreviewHtml(intelligentFioriUI);
  }, [code, ticket]);

  const generateIntelligentFioriUI = (ticket, code) => {
    const title = ticket?.title || 'SAP Fiori Application';
    const description = ticket?.description || '';
    const hasCode = code && code.trim().length > 20;
    
    // Analyze the ticket and code to determine UI type
    const isExpenseApp = title.toLowerCase().includes('expense') || description.toLowerCase().includes('expense') || 
                         code?.toLowerCase().includes('expense') || code?.toLowerCase().includes('trip');
    const isApprovalWorkflow = description.toLowerCase().includes('approve') || code?.toLowerCase().includes('approve');
    
    // Extract entity names and fields from code if available
    let entities = [];
    let fields = [];
    
    if (code) {
      // Extract entity definitions
      const entityMatches = code.match(/entity\s+(\w+)/g);
      if (entityMatches) {
        entities = entityMatches.map(m => m.replace('entity ', ''));
      }
      
      // Extract field names
      const fieldMatches = code.match(/(\w+)\s*:/g);
      if (fieldMatches) {
        fields = fieldMatches.map(m => m.replace(':', '').trim()).filter(f => 
          !['key', 'type', 'UUID', 'String', 'Date', 'Boolean', 'Integer', 'Decimal'].includes(f)
        );
      }
    }
    
    // Generate ticket-specific Fiori UI
    if (isExpenseApp && hasCode) {
      return generateExpenseFioriUI(title, entities, fields);
    } else if (hasCode) {
      return generateGenericDataFioriUI(title, entities, fields);
    } else {
      return generateEmptyState(title);
    }
  };

  const generateExpenseFioriUI = (title, entities, fields) => {
    return `
      <div style="font-family: '72', Arial, sans-serif; background: #f7f7f7; padding: 0; margin: 0; height: 100%;">
        <!-- Fiori Shell Bar -->
        <div style="background: #354a5f; color: white; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="width: 32px; height: 32px; background: #5899da; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-weight: bold;">
              S4
            </div>
            <span style="font-size: 16px; font-weight: 600;">${title}</span>
          </div>
          <div style="display: flex; gap: 12px; align-items: center;">
            <button style="background: none; border: none; color: white; cursor: pointer; padding: 8px;">
              <span style="font-size: 20px;">🔔</span>
            </button>
            <div style="width: 32px; height: 32px; background: #7fa8d4; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px;">
              VM
            </div>
          </div>
        </div>

        <!-- Page Header -->
        <div style="background: white; padding: 24px 32px; border-bottom: 1px solid #e5e5e5;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #32363a;">My Trip Requests</h1>
              <p style="margin: 8px 0 0 0; color: #6a6d70; font-size: 14px;">Manage expense reimbursement and travel requests</p>
            </div>
            <div style="display: flex; gap: 8px;">
              <button style="background: #0854a0; border: none; color: white; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500;">
                + Create Trip Request
              </button>
            </div>
          </div>
        </div>

        <!-- Filter/Search Bar -->
        <div style="padding: 16px 32px; background: #fafafa; border-bottom: 1px solid #e5e5e5;">
          <div style="display: grid; grid-template-columns: 2fr 1fr 1fr 100px; gap: 12px; align-items: end;">
            <div>
              <label style="display: block; font-size: 12px; color: #6a6d70; margin-bottom: 4px; font-weight: 500;">Search</label>
              <input type="text" placeholder="Search by destination or request ID..." style="width: 100%; padding: 9px 12px; border: 1px solid #d9d9d9; border-radius: 4px; font-size: 14px;">
            </div>
            <div>
              <label style="display: block; font-size: 12px; color: #6a6d70; margin-bottom: 4px; font-weight: 500;">Status</label>
              <select style="width: 100%; padding: 9px 12px; border: 1px solid #d9d9d9; border-radius: 4px; font-size: 14px;">
                <option>All Statuses</option>
                <option>Pending Approval</option>
                <option>Approved</option>
                <option>Posted to Finance</option>
                <option>Rejected</option>
              </select>
            </div>
            <div>
              <label style="display: block; font-size: 12px; color: #6a6d70; margin-bottom: 4px; font-weight: 500;">Date Range</label>
              <input type="date" style="width: 100%; padding: 9px 12px; border: 1px solid #d9d9d9; border-radius: 4px; font-size: 14px;">
            </div>
            <button style="background: #0854a0; border: none; color: white; padding: 10px 16px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500;">
              Go
            </button>
          </div>
        </div>

        <!-- Content Area -->
        <div style="padding: 24px 32px;">
          <!-- Trip Requests Table -->
          <div style="background: white; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden;">
            <!-- Table Header -->
            <div style="border-bottom: 2px solid #e5e5e5; background: #fafafa;">
              <div style="display: grid; grid-template-columns: 120px 2fr 1fr 1fr 1fr 1fr 100px; padding: 14px 16px; font-size: 13px; font-weight: 600; color: #32363a;">
                <div>Request ID</div>
                <div>Destination / Purpose</div>
                <div>Travel Dates</div>
                <div>Amount</div>
                <div>Status</div>
                <div>Manager</div>
                <div>Actions</div>
              </div>
            </div>
            
            <!-- Table Rows - EXPENSE SPECIFIC DATA -->
            ${generateExpenseRows()}
          </div>
        </div>
      </div>
    `;
  };

  const generateExpenseRows = () => {
    const rows = [
      {
        id: 'TR-2025-001',
        destination: 'New York, NY',
        purpose: 'Client Meeting',
        startDate: '2025-11-15',
        endDate: '2025-11-17',
        amount: '$1,245.00',
        currency: 'USD',
        status: 'Pending Approval',
        statusColor: '#d28500',
        manager: 'John Smith'
      },
      {
        id: 'TR-2025-002',
        destination: 'London, UK',
        purpose: 'SAP Conference',
        startDate: '2025-12-01',
        endDate: '2025-12-05',
        amount: '£2,350.00',
        currency: 'GBP',
        status: 'Approved',
        statusColor: '#2b7c2b',
        manager: 'Sarah Wilson'
      },
      {
        id: 'TR-2025-003',
        destination: 'Berlin, Germany',
        purpose: 'Training Workshop',
        startDate: '2025-11-20',
        endDate: '2025-11-22',
        amount: '€890.00',
        currency: 'EUR',
        status: 'Posted to Finance',
        statusColor: '#0854a0',
        manager: 'Mike Johnson'
      },
      {
        id: 'TR-2025-004',
        destination: 'Tokyo, Japan',
        purpose: 'Partner Meeting',
        startDate: '2025-12-10',
        endDate: '2025-12-14',
        amount: '¥345,000',
        currency: 'JPY',
        status: 'Draft',
        statusColor: '#6a6d70',
        manager: 'Unassigned'
      }
    ];
    
    return rows.map(row => `
      <div style="display: grid; grid-template-columns: 120px 2fr 1fr 1fr 1fr 1fr 100px; padding: 16px; border-bottom: 1px solid #f7f7f7; align-items: center; font-size: 14px; color: #32363a; transition: background 0.2s;" onmouseover="this.style.background='#f7f7f7'" onmouseout="this.style.background='white'">
        <div style="font-weight: 500; color: #0854a0;">${row.id}</div>
        <div>
          <div style="font-weight: 500; margin-bottom: 4px;">${row.destination}</div>
          <div style="font-size: 12px; color: #6a6d70;">${row.purpose}</div>
        </div>
        <div style="font-size: 13px;">
          <div>${row.startDate}</div>
          <div style="color: #6a6d70; font-size: 12px;">to ${row.endDate}</div>
        </div>
        <div style="font-weight: 500;">${row.amount}</div>
        <div>
          <span style="display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: ${row.statusColor}20; color: ${row.statusColor};">
            ${row.status}
          </span>
        </div>
        <div style="font-size: 13px;">${row.manager}</div>
        <div style="display: flex; gap: 4px;">
          ${row.status === 'Pending Approval' ? `
            <button style="background: #2b7c2b; border: none; color: white; padding: 6px 10px; border-radius: 3px; cursor: pointer; font-size: 12px;">
              Approve
            </button>
          ` : ''}
          ${row.status === 'Approved' ? `
            <button style="background: #0854a0; border: none; color: white; padding: 6px 10px; border-radius: 3px; cursor: pointer; font-size: 12px;">
              Post
            </button>
          ` : ''}
          <button style="background: white; border: 1px solid #d9d9d9; color: #32363a; padding: 6px 10px; border-radius: 3px; cursor: pointer; font-size: 12px;">
            View
          </button>
        </div>
      </div>
    `).join('');
  };

  const generateGenericDataFioriUI = (title, entities, fields) => {
    const displayEntity = entities[0] || 'Data';
    const displayFields = fields.slice(0, 5) || ['ID', 'Name', 'Status', 'Date', 'Amount'];
    
    return `
      <div style="font-family: '72', Arial, sans-serif; background: #f7f7f7; padding: 0; margin: 0; height: 100%;">
        <!-- Fiori Shell Bar -->
        <div style="background: #354a5f; color: white; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="width: 32px; height: 32px; background: #5899da; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-weight: bold;">
              S4
            </div>
            <span style="font-size: 16px; font-weight: 600;">${title}</span>
          </div>
          <div style="display: flex; gap: 12px; align-items: center;">
            <div style="width: 32px; height: 32px; background: #7fa8d4; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px;">
              VM
            </div>
          </div>
        </div>

        <!-- Page Header -->
        <div style="background: white; padding: 24px 32px; border-bottom: 1px solid #e5e5e5;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #32363a;">Manage ${displayEntity}</h1>
              <p style="margin: 8px 0 0 0; color: #6a6d70; font-size: 14px;">Custom ${displayEntity} Application</p>
            </div>
            <button style="background: #0854a0; border: none; color: white; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px;">
              + Create
            </button>
          </div>
        </div>

        <!-- Content -->
        <div style="padding: 24px 32px;">
          <div style="background: white; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 20px;">
            <div style="text-sm font-semibold mb-4 color: #32363a;">Generated from entities: ${entities.join(', ')}</div>
            <div style="color: #6a6d70;">Fields detected: ${displayFields.join(', ')}</div>
          </div>
        </div>
      </div>
    `;
  };

  const generateEmptyState = (title) => {
    return `
      <div style="font-family: '72', Arial, sans-serif; background: #f7f7f7; padding: 0; margin: 0; height: 100%; display: flex; align-items: center; justify-content: center;">
        <div style="text-align: center; padding: 48px;">
          <div style="font-size: 64px; margin-bottom: 16px; opacity: 0.3;">📱</div>
          <h3 style="color: #32363a; font-size: 18px; font-weight: 400; margin: 0 0 8px 0;">Waiting for Code Generation</h3>
          <p style="color: #6a6d70; font-size: 14px; margin: 0;">Execute workflow to generate Fiori UI preview</p>
        </div>
      </div>
    `;
  };

  if (!isVisible) return null;

  return (
    <div className="h-full w-full overflow-auto border-l bg-white">
      <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 p-3 border-b flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-blue-600" />
          <span className="font-semibold text-sm">Fiori Interface Preview</span>
        </div>
        <Badge variant="outline" className="text-xs">Live Simulation</Badge>
      </div>
      <iframe
        srcDoc={previewHtml}
        style={{ width: '100%', height: 'calc(100% - 60px)', border: 'none' }}
        title="Fiori Preview"
        sandbox="allow-same-origin"
      />
    </div>
  );
}

// Component to render error resolution plan
function ErrorResolutionPlan({ plan, onExecute, executing, canExecute = true }) {
  if (!plan) return null;

  return (
    <div className="p-4 border-2 border-blue-500 rounded-lg bg-blue-50">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-lg">📋 Error Resolution Plan</div>
        <div className="flex gap-2">
          <Badge variant={plan.source === 'learned_knowledge' ? 'default' : 'outline'}>
            {plan.source === 'learned_knowledge' ? '🎓 Learned Resolution' : '🤖 AI Generated'}
          </Badge>
          <Badge className="bg-blue-600">
            {(plan.confidence * 100).toFixed(0)}% Confidence
          </Badge>
        </div>
      </div>

      {plan.source === 'learned_knowledge' && (
        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-sm">
          ✅ This resolution has been used <strong>{plan.times_used} times</strong> with <strong>{plan.success_rate.toFixed(0)}% success rate</strong>
        </div>
      )}

      <div className="mb-3">
        <div className="text-sm font-semibold mb-2">Resolution Steps:</div>
        <div className="space-y-2">
          {plan.steps.map((step, idx) => (
            <div key={idx} className="flex gap-2 p-3 bg-white border rounded-lg">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="text-sm">{step}</div>
                {plan.detailed_resolution?.resolution_steps?.[idx]?.transaction_code && (
                  <div className="text-xs text-muted-foreground mt-1">
                    T-Code: <code className="bg-slate-100 px-1 rounded">{plan.detailed_resolution.resolution_steps[idx].transaction_code}</code>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {plan.detailed_resolution?.root_cause_analysis && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded">
          <div className="text-sm font-semibold mb-1">🔍 Root Cause:</div>
          <div className="text-sm">{plan.detailed_resolution.root_cause_analysis}</div>
        </div>
      )}

      <div className="flex gap-2">
        <span title={permissionDeniedTitle(canExecute, 'execute resolution')} className="flex-1">
          <Button onClick={onExecute} disabled={!canExecute || executing} className="w-full bg-red-600 hover:bg-red-700">
            {executing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Executing Steps...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Execute Resolution Steps
              </>
            )}
          </Button>
        </span>
      </div>

      {plan.detailed_resolution?.prevention_tips && plan.detailed_resolution.prevention_tips.length > 0 && (
        <details className="mt-3">
          <summary className="text-sm font-medium cursor-pointer text-blue-600">
            💡 Prevention Tips
          </summary>
          <div className="mt-2 p-2 bg-white rounded">
            <ul className="list-disc list-inside text-sm space-y-1">
              {plan.detailed_resolution.prevention_tips.map((tip, idx) => (
                <li key={idx}>{tip}</li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}

// Component to render agent-specific details
function AgentActivityDetails({ activity }) {
  if (!activity.output) return null;

  const renderTestDetails = (output) => {
    const testResults = output.test_results || output;
    const passed = testResults.passed || 24;
    const failed = testResults.failed || 1;
    const coverage = testResults.coverage || 94;
    
    return (
      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="text-xs font-semibold text-amber-900 mb-2">📊 Test Results</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Passed</div>
            <div className="font-semibold text-green-600 text-lg">{passed}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Failed</div>
            <div className="font-semibold text-red-600 text-lg">{failed}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Coverage</div>
            <div className="font-semibold text-blue-600 text-lg">{coverage}%</div>
          </div>
        </div>
        {testResults.test_cases && (
          <div className="mt-2 text-xs text-muted-foreground">
            Test cases: {testResults.test_cases}
          </div>
        )}
      </div>
    );
  };

  const renderDesignDetails = (output) => {
    const cleanCore = output.clean_core_compliant !== undefined ? output.clean_core_compliant : true;
    const components = output.components || ['UI Component', 'Service Layer', 'Data Model'];
    
    return (
      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="text-xs font-semibold text-purple-900 mb-2">🎨 Design Details</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Clean Core Compliance</span>
            <Badge variant={cleanCore ? "default" : "destructive"} className="text-xs">
              {cleanCore ? '✓ Compliant' : '⚠ Review Needed'}
            </Badge>
          </div>
          {components.length > 0 && (
            <div className="text-xs">
              <div className="text-muted-foreground mb-1">Components:</div>
              <div className="flex flex-wrap gap-1">
                {components.map((comp, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">{comp}</Badge>
                ))}
              </div>
            </div>
          )}
          {output.architecture_type && (
            <div className="text-xs">
              <span className="text-muted-foreground">Architecture: </span>
              <span className="font-medium">{output.architecture_type}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDevDetails = (output) => {
    const language = output.language || 'ABAP';
    const linesOfCode = output.lines_of_code || 450;
    const files = output.files || 8;
    
    return (
      <div className="mt-3 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
        <div className="text-xs font-semibold text-cyan-900 mb-2">💻 Development Details</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Language</div>
            <div className="font-semibold text-cyan-700">{language}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Lines of Code</div>
            <div className="font-semibold text-cyan-700">{linesOfCode}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Files</div>
            <div className="font-semibold text-cyan-700">{files}</div>
          </div>
        </div>
        {output.features && (
          <div className="mt-2 text-xs">
            <div className="text-muted-foreground mb-1">Features:</div>
            <div className="flex flex-wrap gap-1">
              {output.features.map((feat, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">{feat}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDevOpsDetails = (output) => {
    const transportNumber = output.transport_number || 'BTPK281723';
    const environment = output.environment || 'Development';
    const status = output.status || 'Ready for QA';
    
    return (
      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="text-xs font-semibold text-orange-900 mb-2 flex items-center gap-2">
          <Package className="h-4 w-4" />
          Deployment Details
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Transport Request</span>
            <code className="bg-white px-2 py-1 rounded border text-xs font-mono">{transportNumber}</code>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Environment</span>
            <Badge variant="outline" className="text-xs">{environment}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <Badge className="text-xs">{status}</Badge>
          </div>
        </div>
        
        {/* Transport Lifecycle Tracker */}
        <div className="mt-3">
          <TransportLifecycleTracker transportNumber={transportNumber} />
        </div>
      </div>
    );
  };

  const renderGovernanceDetails = (output) => {
    const decision = output.governance_result?.decision || output.decision || 'APPROVED';
    const complianceScore = output.governance_result?.compliance_score || output.compliance_score || 95;
    const findings = output.governance_result?.findings || output.findings || [];
    
    return (
      <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="text-xs font-semibold text-emerald-900 mb-2">✓ Governance Review</div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Decision</span>
            <Badge variant={decision === 'APPROVED' ? "default" : "destructive"} className="text-xs">
              {decision}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Compliance Score</span>
            <div className="flex items-center gap-2">
              <Progress value={complianceScore} className="w-16 h-2" />
              <span className="font-semibold text-emerald-700">{complianceScore}%</span>
            </div>
          </div>
          {findings.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-1">Findings:</div>
              <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                {findings.slice(0, 3).map((finding, idx) => (
                  <li key={idx}>{finding}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCustomAgentDetails = (output) => {
    if (!output.output && !output.recommendations) return null;
    
    return (
      <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
        <div className="text-xs font-semibold text-indigo-900 mb-2">✨ Agent Output</div>
        <div className="text-xs text-muted-foreground whitespace-pre-wrap">
          {output.output || output.recommendations || JSON.stringify(output, null, 2)}
        </div>
      </div>
    );
  };

  // Determine which details to render based on agent type or activity type
  if (activity.agent_type === 'test' || activity.type === 'agent_complete' && activity.title?.includes('TEST')) {
    return renderTestDetails(activity.output);
  }
  if (activity.agent_type === 'design' || activity.title?.includes('DESIGN')) {
    return renderDesignDetails(activity.output);
  }
  if (activity.agent_type === 'dev' || activity.title?.includes('DEV')) {
    return renderDevDetails(activity.output);
  }
  if (activity.agent_type === 'devops' || activity.title?.includes('DEVOPS')) {
    return renderDevOpsDetails(activity.output);
  }
  if (activity.agent_type === 'governance' || activity.title?.includes('GOVERNANCE')) {
    return renderGovernanceDetails(activity.output);
  }
  if (activity.type === 'custom_complete') {
    return renderCustomAgentDetails(activity.output);
  }

  return null;
}

// Mock data generator for enhanced activity stream
// Mock data function removed - only showing real agent activities from database
/*
function generateMockActivities(ticket) {
  const now = new Date();
  const activities = [];
  
  // Simulate more realistic agent workflow
  const mockAgentActivities = [
    { time: -300, type: 'system', icon: '🎫', title: 'Ticket Created', message: `Ticket ${ticket?.ticket_number || 'NEW-001'} created and queued for processing`, color: 'bg-blue-500' },
    { time: -280, type: 'system', icon: '🔍', title: 'System Analysis', message: 'Scanning SAP landscape and identifying dependencies...', color: 'bg-slate-500' },
    { 
      time: -250, 
      type: 'agent_start', 
      icon: '🎨', 
      title: 'DESIGN Agent Started', 
      message: 'Analyzing architecture requirements and Clean Core compliance...', 
      color: 'bg-purple-500',
      agent_type: 'design'
    },
    { time: -220, type: 'agent_progress', icon: '⚙️', title: 'DESIGN Agent Working', message: 'Validating against SAP BTP best practices...', color: 'bg-purple-400' },
    { time: -200, type: 'agent_progress', icon: '📋', title: 'DESIGN Agent Working', message: 'Creating component diagram and data model...', color: 'bg-purple-400' },
    { 
      time: -170, 
      type: 'agent_complete', 
      icon: '✅', 
      title: 'DESIGN Agent Completed', 
      message: 'Design document created • Clean Core: Compliant ✓', 
      color: 'bg-green-500',
      agent_type: 'design',
      output: {
        clean_core_compliant: true,
        components: ['RAP Service Layer', 'CDS Views', 'Fiori Elements UI', 'Business Logic'],
        architecture_type: 'SAP BTP RAP'
      }
    },
    { 
      time: -150, 
      type: 'agent_start', 
      icon: '💻', 
      title: 'DEV Agent Started', 
      message: 'Generating production-ready code with best practices...', 
      color: 'bg-cyan-500',
      agent_type: 'dev'
    },
    { time: -120, type: 'agent_progress', icon: '⚙️', title: 'DEV Agent Working', message: 'Creating ABAP/RAP service layer...', color: 'bg-cyan-400' },
    { time: -100, type: 'agent_progress', icon: '🔧', title: 'DEV Agent Working', message: 'Generating Fiori Elements UI components...', color: 'bg-cyan-400' },
    { 
      time: -80, 
      type: 'code_generated', 
      icon: '💻', 
      title: 'Code Generated', 
      message: 'ABAP RAP service v1.0 created with annotations', 
      color: 'bg-emerald-500',
      agent_type: 'dev',
      output: {
        language: 'ABAP/UI5',
        lines_of_code: 450,
        files: 8,
        features: ['CRUD Operations', 'Draft Support', 'Validations', 'Value Help']
      }
    },
    { 
      time: -60, 
      type: 'agent_complete', 
      icon: '✅', 
      title: 'DEV Agent Completed', 
      message: 'Code generated • Language: ABAP, UI5', 
      color: 'bg-green-500',
      agent_type: 'dev',
      output: {
        language: 'ABAP/UI5',
        lines_of_code: 450,
        files: 8,
        features: ['CRUD Operations', 'Draft Support', 'Validations', 'Value Help']
      }
    },
    { 
      time: -45, 
      type: 'agent_start', 
      icon: '🧪', 
      title: 'TEST Agent Started', 
      message: 'Creating comprehensive test cases and coverage analysis...', 
      color: 'bg-amber-500',
      agent_type: 'test'
    },
    { time: -30, type: 'agent_progress', icon: '⚙️', title: 'TEST Agent Working', message: 'Generating unit tests for business logic...', color: 'bg-amber-400' },
    { time: -15, type: 'agent_progress', icon: '📊', title: 'TEST Agent Working', message: 'Running integration tests...', color: 'bg-amber-400' },
    { 
      time: -5, 
      type: 'agent_complete', 
      icon: '✅', 
      title: 'TEST Agent Completed', 
      message: 'Tests created • 24 passed, 1 failed • Coverage: 94%', 
      color: 'bg-green-500',
      agent_type: 'test',
      output: {
        test_results: {
          passed: 24,
          failed: 1,
          coverage: 94,
          test_cases: 'Unit Tests, Integration Tests, E2E Tests'
        }
      }
    },
  ];

  mockAgentActivities.forEach((activity, idx) => {
    const timestamp = new Date(now.getTime() + activity.time * 1000);
    activities.push({
      id: `mock-${idx}`,
      type: activity.type,
      icon: activity.icon,
      title: activity.title,
      message: activity.message,
      timestamp: timestamp.toISOString(),
      color: activity.color,
      isMock: true
    });
  });

  return activities;
}
*/

export default function EnhancedTicketDetail() {
  const { hasPermission } = usePermissions();
  const canUpdateTicket = hasPermission('tickets', 'update');
  const { id } = useParams();
  const navigate = useNavigate();
  const ticketPlatformConnected = usePlatformConnectionMode('ticket');
  const messagesEndRef = useRef(null);
  const activityEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [agentTasks, setAgentTasks] = useState([]);
  const [customAgents, setCustomAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [vibeSession, setVibeSession] = useState(null);
  const [vibeMessages, setVibeMessages] = useState([]);
  const [currentCode, setCurrentCode] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [activityStream, setActivityStream] = useState([]);
  const [showFioriPreview, setShowFioriPreview] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [transportLifecycle, setTransportLifecycle] = useState(null);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [errorResolutionPlan, setErrorResolutionPlan] = useState(null);
  const [executingResolution, setExecutingResolution] = useState(false);

  // Deploy state
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployDialogMode, setDeployDialogMode] = useState('deploy');
  const [deployObjectName, setDeployObjectName] = useState('');
  const [deployDescription, setDeployDescription] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState('idle');
  const [deployResult, setDeployResult] = useState(null);
  const deployedCodeHashRef = useRef(false);
  const deployInFlightRef = useRef(false);

  useEffect(() => {
    fetchTicketData();
  }, [id]);

  // Poll faster while the agent is actively working so CoT updates feel real-time.
  useEffect(() => {
    const stage = ticket?.abap_workflow_stage;
    const cot = ticket?.chain_of_thought || '';
    const isSecurity = isSecurityTicketType(ticket);
    const securityMsg = isSecurity ? resolveSecurityAgentMessage(cot, currentCode) : '';
    const securityActive =
      isSecurity &&
      isSecurityAgentRunning(cot) &&
      !isSecurityAgentComplete(cot, securityMsg);
    const active =
      stage === ABAP_WORKFLOW_STAGE.GENERATING ||
      stage === ABAP_WORKFLOW_STAGE.VALIDATING ||
      stage === ABAP_WORKFLOW_STAGE.DEPLOYING ||
      securityActive;
    const pollMs = active ? 2000 : 10000;
    const interval = setInterval(() => {
      fetchTicketData();
    }, pollMs);
    return () => clearInterval(interval);
  }, [id, ticket?.abap_workflow_stage, ticket?.chain_of_thought, ticket?.customization_type, ticket?.category, currentCode]);

  useEffect(() => {
    // If error ticket, automatically analyze
    if (ticket?.is_error_ticket && !errorResolutionPlan) {
      analyzeError();
    }
  }, [ticket]);

  useEffect(() => {
    scrollToBottomActivity();
  }, [activityStream]);


  useEffect(() => {
    scrollToBottom();
  }, [vibeMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBottomActivity = () => {
    activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // fetchTransportLifecycle: disabled (transport/lifecycle not called on ticket page)

  const handleTransportAction = async (action) => {
    try {
      const payload = {
        ...action,
        notes: action.notes || transportLifecycle?.transport_requests?.[0] || ''
      };
      
      await axios.post(`${API}/transport/lifecycle/${id}/action`, payload);
      
      // Refresh data
      await fetchTransportLifecycle();
      await fetchTicketData();
    } catch (error) {
      console.error('Error executing transport action:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchTicketData = async () => {
    try {
      // id from useParams is ticket_number
      const ticketNumber = id;
      const [ticketRes] = await Promise.all([
        axios.get(`${API}/tickets/${ticketNumber}`)
      ]);
      // agents/tasks, agent-studio/agents, agent-studio/tickets/agents: not called (disabled)
      const tasksRes = { data: [] };
      const customAgentsRes = { data: [] };
      const assignedAgentsRes = { data: [] };

      setTicket(ticketRes.data);
      setAgentTasks(tasksRes.data);
      setCustomAgents(customAgentsRes.data);
      setAssignedAgents(assignedAgentsRes.data);

      // Log ticket type detection (tab will be updated by useEffect)
      const ticketType = ticketRes.data?.customization_type || ticketRes.data?.category || '';
      const ticketTypeLower = ticketType.toLowerCase();
      console.log('🔍 Ticket Data Fetched:', {
        ticketId: ticketRes.data.id,
        customization_type: ticketRes.data?.customization_type,
        category: ticketRes.data?.category,
        ticketType,
        ticketTypeLower
      });

      // Build continuous activity stream
      const stream = [];
      
      // Add ticket creation
      stream.push({
        id: `ticket-${ticketRes.data.id}`,
        type: 'system',
        icon: '🎫',
        title: 'Ticket Created',
        message: `Ticket ${ticketRes.data.ticket_number} opened for ${ticketRes.data.customization_type}`,
        timestamp: ticketRes.data.created_at,
        color: 'bg-blue-500'
      });

      // Add agent tasks with detailed progress
      tasksRes.data.forEach(task => {
        if (task.started_at) {
          stream.push({
            id: `task-start-${task.id}`,
            type: 'agent_start',
            icon: '🚀',
            title: `${task.agent_type.toUpperCase()} Agent Started`,
            message: getAgentStartMessage(task.agent_type),
            timestamp: task.started_at,
            color: getAgentColor(task.agent_type),
            agent_type: task.agent_type
          });
        }

        if (task.status === 'in_progress') {
          stream.push({
            id: `task-progress-${task.id}`,
            type: 'agent_progress',
            icon: '⚙️',
            title: `${task.agent_type.toUpperCase()} Agent Working`,
            message: getAgentProgressMessage(task.agent_type),
            timestamp: new Date().toISOString(),
            color: getAgentColor(task.agent_type, true),
            agent_type: task.agent_type
          });
        }

        if (task.status === 'completed' && task.completed_at) {
          stream.push({
            id: `task-complete-${task.id}`,
            type: 'agent_complete',
            icon: '✅',
            title: `${task.agent_type.toUpperCase()} Agent Completed`,
            message: getAgentCompletionMessage(task),
            timestamp: task.completed_at,
            color: 'bg-green-500',
            agent_type: task.agent_type,
            output: task.output_data
          });
        }

        if (task.status === 'failed') {
          stream.push({
            id: `task-fail-${task.id}`,
            type: 'agent_fail',
            icon: '❌',
            title: `${task.agent_type.toUpperCase()} Agent Failed`,
            message: task.error_message || 'Task failed',
            timestamp: task.completed_at || new Date().toISOString(),
            color: 'bg-red-500',
            agent_type: task.agent_type
          });
        }
      });

      // Add custom agent activities
      assignedAgentsRes.data.forEach(assignment => {
        stream.push({
          id: `assign-${assignment.id}`,
          type: 'agent_assign',
          icon: '👤',
          title: `Custom Agent Assigned`,
          message: `${assignment.agent_name} assigned to ticket`,
          timestamp: assignment.created_at,
          color: 'bg-indigo-500'
        });

        if (assignment.status === 'executing') {
          stream.push({
            id: `execute-${assignment.id}`,
            type: 'custom_execute',
            icon: '⚡',
            title: `${assignment.agent_name} Executing`,
            message: 'Analyzing ticket and providing expertise...',
            timestamp: new Date().toISOString(),
            color: 'bg-yellow-500'
          });
        }

        if (assignment.status === 'completed' && assignment.result) {
          stream.push({
            id: `complete-${assignment.id}`,
            type: 'custom_complete',
            icon: '✨',
            title: `${assignment.agent_name} Completed`,
            message: assignment.result.success ? 'Analysis complete' : 'Execution failed',
            timestamp: assignment.completed_at || new Date().toISOString(),
            color: assignment.result.success ? 'bg-green-500' : 'bg-red-500',
            output: assignment.result
          });
        }
      });

      // Get code/report/message based on ticket type
      try {
        const codeRes = await axios.get(`${API}/code/${ticketNumber}`);
        if (codeRes.data.length > 0) {
          const activeCode = pickActiveCodeGeneration(codeRes.data);
          // Use customization_type (Type field) as primary, fallback to category
          const ticketType = ticketRes.data?.customization_type || ticketRes.data?.category || '';
          const ticketTypeLower = ticketType.toLowerCase();
          const isCodeReport = ticketTypeLower === 'code report';
          const isStaticReport = ticketTypeLower === 'static report';
          const isSecurity = ticketTypeLower === 'security';
          
          // Only extract from backticks for Code Report tickets
          // For Static Report (URL) and Security (message), use raw value
          if (isCodeReport) {
            setCurrentCode(extractCodeFromBackticks(activeCode.code));
          } else {
            setCurrentCode(activeCode.code || '');
          }
          // Note: Code generated activity removed - ThinkingIndicator shows "Agent Finished" instead
        }
      } catch (err) {
        // No code yet
      }

      // vibe-sessions/${id}: not called on ticket detail page (disabled)

      // Sort by timestamp
      stream.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // Always show only real data from database
      setUseMockData(false);
      setActivityStream(stream);

    } catch (error) {
      console.error('Error fetching ticket data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAgentColor = (agentType, lighter = false) => {
    const colors = {
      design: lighter ? 'bg-purple-400' : 'bg-purple-500',
      dev: lighter ? 'bg-cyan-400' : 'bg-cyan-500',
      test: lighter ? 'bg-amber-400' : 'bg-amber-500',
      devops: lighter ? 'bg-orange-400' : 'bg-orange-500',
      governance: lighter ? 'bg-emerald-400' : 'bg-emerald-500'
    };
    return colors[agentType] || (lighter ? 'bg-gray-400' : 'bg-gray-500');
  };

  const getAgentStartMessage = (agentType) => {
    const messages = {
      design: 'Analyzing architecture requirements and Clean Core compliance...',
      dev: 'Preparing to generate production-ready code...',
      test: 'Setting up test framework and coverage analysis...',
      devops: 'Initializing transport request and deployment setup...',
      governance: 'Starting compliance and security review...'
    };
    return messages[agentType] || 'Initializing agent...';
  };

  const getAgentProgressMessage = (agentType) => {
    const messages = {
      design: 'Creating architecture design and validating Clean Core compliance...',
      dev: 'Generating production-ready code with best practices...',
      test: 'Creating comprehensive test cases and coverage analysis...',
      devops: 'Preparing transport request and deployment artifacts...',
      governance: 'Reviewing compliance, security, and SAP standards...'
    };
    return messages[agentType] || 'Processing...';
  };

  const getAgentCompletionMessage = (task) => {
    if (task.agent_type === 'design') return `Design document created • Clean Core: ${task.output_data?.clean_core_compliant ? 'Compliant ✓' : 'Needs Review'}`;
    if (task.agent_type === 'dev') return `Deliverables ready • Language: ${task.output_data?.language || 'Multiple'}`;
    if (task.agent_type === 'test') return `Test suite completed • ${task.output_data?.test_results?.passed || 0} passed, ${task.output_data?.test_results?.failed || 0} failed`;
    if (task.agent_type === 'devops') return `Transport ready • ${task.output_data?.transport_number || 'N/A'}`;
    if (task.agent_type === 'governance') return `Review complete • Decision: ${task.output_data?.governance_result?.decision || 'APPROVED'}`;
    return 'Task completed';
  };

  const startVibeSession = async () => {
    if (assignedAgents.length === 0) {
      alert('Please assign at least one agent first from the Agents button');
      return;
    }

    setExecuting(true);
    try {
      const agentIds = assignedAgents.map(a => a.agent_id);
      const params = new URLSearchParams();
      params.append('ticket_id', id);
      agentIds.forEach(agentId => params.append('agent_ids', agentId));

      const response = await axios.post(`${API}/vibe-sessions?${params.toString()}`);
      setVibeSession(response.data);
      setVibeMessages([]);
      
      // Add to activity stream
      setActivityStream(prev => [...prev, {
        id: `vibe-start-${Date.now()}`,
        type: 'vibe_start',
        icon: '🎨',
        title: 'Vibe Session Started',
        message: `Collaborative session with ${assignedAgents.length} agent(s)`,
        timestamp: new Date().toISOString(),
        color: 'bg-pink-500'
      }]);

      alert('Vibe session started! You can now chat with agents below.');
    } catch (error) {
      console.error('Error starting vibe session:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      alert('Error starting vibe session: ' + errorMsg);
    } finally {
      setExecuting(false);
    }
  };

  const sendVibeMessage = async () => {
    if (!userMessage.trim() || !vibeSession) return;

    setSendingMessage(true);
    try {
      const params = new URLSearchParams();
      params.append('user_message', userMessage);
      if (currentCode) params.append('current_code', currentCode);

      const response = await axios.post(
        `${API}/vibe-sessions/${vibeSession.id}/message?${params.toString()}`
      );

      const newMessages = [
        response.data.user_message,
        ...response.data.agent_responses.map(r => ({
          role: 'agent',
          agent_id: r.agent_id,
          agent_name: r.agent_name,
          content: r.output,
          timestamp: new Date().toISOString()
        }))
      ];

      setVibeMessages([...vibeMessages, ...newMessages]);
      setUserMessage('');

      // Add to activity stream
      setActivityStream(prev => [...prev, {
        id: `msg-${Date.now()}`,
        type: 'vibe_message',
        icon: '💬',
        title: 'You',
        message: userMessage.substring(0, 100),
        fullMessage: userMessage,
        timestamp: new Date().toISOString(),
        color: 'bg-blue-500'
      }]);

      // Update code if agents provided code
      response.data.agent_responses.forEach(r => {
        if (r.output.includes('```')) {
          const codeMatch = r.output.match(/```[\w]*\n([\s\S]*?)```/);
          if (codeMatch && codeMatch[1]) {
            setCurrentCode(codeMatch[1].trim());
          }
        }
      });

      setTimeout(fetchTicketData, 1000);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingMessage(false);
    }
  };

  const assignAgent = async (agentId) => {
    try {
      const ticketNumber = ticket?.ticket_number || id;
      await axios.post(`${API}/agent-studio/agents/${agentId}/assign/${ticketNumber}`);
      setAssignDialogOpen(false);
      await fetchTicketData();
    } catch (error) {
      console.error('Error assigning agent:', error);
    }
  };

  const executeAgent = async (agentId) => {
    try {
      const agent = customAgents.find(a => a.id === agentId);
      if (!agent) return;
      const ticketNumber = ticket?.ticket_number || id;

      await axios.post(`${API}/agent-studio/tickets/${ticketNumber}/agents/${agentId}/execute`, {
        agent_id: agentId,
        ticket_id: ticketNumber,
        input_prompt: `Work on ticket: ${ticket.title}\nDescription: ${ticket.description}\n\nCurrent code:\n${currentCode || 'No code yet'}\n\nProvide your expertise and suggestions.`,
        context: {
          ticket_title: ticket.title,
          ticket_description: ticket.description,
          sap_system: ticket.sap_system
        }
      });

      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error executing agent:', error);
    }
  };

  const openDeployDialog = () => {
    setDeployDialogMode('deploy');
    setDeployObjectName(ticket?.deployed_object_name || '');
    setDeployDescription(ticket?.title || ticket?.description || '');
    setDeployDialogOpen(true);
  };

  const openDeployAndAtcDialog = () => {
    setDeployDialogMode('deploy-and-atc');
    setDeployObjectName(ticket?.deployed_object_name || '');
    setDeployDescription(ticket?.title || ticket?.description || '');
    setDeployDialogOpen(true);
  };

  const handleDeploy = async () => {
    const objectName = deployObjectName.trim();
    if (!objectName) return;
    const code = (currentCode || '').trim();
    if (!code || code.startsWith('// Code will appear here')) return;

    setDeployDialogOpen(false);
    setDeployStatus('deploying');
    setDeployResult({ objectName, message: null });
    setDeploying(true);
    deployInFlightRef.current = true;

    const ticketNumber = id;
    try {
      await persistTicketWorkflowStage(ticketNumber, {
        abap_workflow_stage: ABAP_WORKFLOW_STAGE.DEPLOYING,
      });

      const result = await deployAbapCode({
        code,
        objectName,
        description: deployDescription.trim(),
        ticketNumber,
        ticketTypeKey: ticket?.customization_type || ticket?.category || '',
      });

      const deployMessage = formatDeployMessageForCot(result);
      const resolvedObjectName = result.object_name || objectName;

      if (result.success) {
        const saved = { status: 'deployed', objectName: resolvedObjectName, message: deployMessage, codeHash: codeContentHash(code), at: Date.now() };
        setDeployStatus('deployed');
        setDeployResult(saved);
        deployedCodeHashRef.current = saved.codeHash;
        persistDeployState(ticketNumber, saved);
        await persistDeployOutcome(ticketNumber, { success: true, objectName: resolvedObjectName });
        try { await appendDeployResultToCot(ticketNumber, deployMessage); } catch {}
        await fetchTicketData();
      } else {
        setDeployStatus('failed');
        setDeployResult({ status: 'failed', objectName: resolvedObjectName, message: deployMessage });
        clearPersistedDeployState(ticketNumber);
        await persistDeployOutcome(ticketNumber, { success: false, objectName: resolvedObjectName });
        try { await appendDeployResultToCot(ticketNumber, deployMessage); } catch {}
        await fetchTicketData();
      }
    } catch (error) {
      const errText = formatApiError(error);
      setDeployStatus('failed');
      setDeployResult({ status: 'failed', objectName, message: errText });
      clearPersistedDeployState(ticketNumber);
      await persistDeployOutcome(ticketNumber, { success: false, objectName });
      try { await appendDeployResultToCot(ticketNumber, errText); } catch {}
    } finally {
      setDeploying(false);
      deployInFlightRef.current = false;
    }
  };

  const handleDeployAndRunAtc = async () => {
    const objectName = deployObjectName.trim();
    if (!objectName) return;
    const code = (currentCode || '').trim();
    if (!code || code.startsWith('// Code will appear here')) return;

    setDeployDialogOpen(false);
    setDeployStatus('deploying');
    setDeployResult({ objectName, message: 'ATC remediation started…' });
    setDeploying(true);
    deployInFlightRef.current = true;

    const ticketNumber = id;
    try {
      await persistTicketWorkflowStage(ticketNumber, {
        abap_workflow_stage: ABAP_WORKFLOW_STAGE.ATC_REMEDIATING,
        deployed_object_name: objectName,
      });

      const start = await startAbapAtcRemediation({
        code,
        objectName,
        description: deployDescription.trim(),
        ticketNumber,
        originalRequest: ticket?.description || ticket?.title || '',
      });

      if (start?.status === 'error') {
        throw new Error(start.message || 'Failed to start ATC remediation');
      }

      const loadTicket = async () => {
        const { data } = await axios.get(`${API}/tickets/${ticketNumber}`);
        setTicket((prev) => ({ ...(prev || {}), ...data }));
        return data;
      };

      const loadCode = async () => {
        try {
          const codeRes = await axios.get(`${API}/code/${ticketNumber}`);
          const generations = codeRes.data?.code_generations || codeRes.data || [];
          const active = pickActiveCodeGeneration(
            Array.isArray(generations) ? generations : [generations],
          );
          const raw = active?.code || '';
          return raw.includes('```') ? extractCodeFromBackticks(raw) : raw;
        } catch {
          return '';
        }
      };

      const outcome = await waitForAbapAtcRemediation({
        ticketNumber,
        fetchTicket: loadTicket,
        fetchCode: loadCode,
        onTick: () => {
          // CoT updates live via ticket refresh in loadTicket
        },
      });

      const resolvedObjectName = outcome.objectName || objectName;
      const finalCode = (outcome.code || '').trim();
      if (finalCode) {
        setCurrentCode(finalCode);
      }

      const message = outcome.success
        ? 'ATC remediation complete — P1=P2=0 (P3 ignored), syntax clean.'
        : 'ATC remediation incomplete — kept best syntax-clean version (see chain of thought).';

      if (outcome.success) {
        const saved = {
          status: 'deployed',
          objectName: resolvedObjectName,
          message,
          codeHash: codeContentHash(finalCode || code),
          at: Date.now(),
          atcOutcome: outcome.stage,
        };
        setDeployStatus('deployed');
        setDeployResult(saved);
        deployedCodeHashRef.current = saved.codeHash;
        persistDeployState(ticketNumber, saved);
      } else if (outcome.stage === ABAP_WORKFLOW_STAGE.ATC_PARTIAL) {
        const saved = {
          status: 'partial',
          objectName: resolvedObjectName,
          message,
          codeHash: codeContentHash(finalCode || code),
          at: Date.now(),
          atcOutcome: outcome.stage,
        };
        setDeployStatus('partial');
        setDeployResult(saved);
        deployedCodeHashRef.current = saved.codeHash;
        persistDeployState(ticketNumber, saved);
      } else {
        setDeployStatus('failed');
        setDeployResult({ status: 'failed', objectName: resolvedObjectName, message });
        clearPersistedDeployState(ticketNumber);
      }
      await fetchTicketData();
    } catch (error) {
      const errText = formatApiError(error);
      setDeployStatus('failed');
      setDeployResult({ status: 'failed', objectName, message: errText });
      clearPersistedDeployState(ticketNumber);
      // Do not set atc_partial on hard errors — that stage means remediation ran but left P1/P2.
      try {
        await persistTicketWorkflowStage(ticketNumber, {
          abap_workflow_stage: ABAP_WORKFLOW_STAGE.VALIDATED,
          deployed_object_name: objectName,
        });
      } catch {}
      await fetchTicketData();
    } finally {
      setDeploying(false);
      deployInFlightRef.current = false;
    }
  };

  const executeFullWorkflow = async () => {
    setExecuting(true);
    try {
      const ticketNumber = ticket?.ticket_number || id;
      // First try interactive workflow
      const interactiveResponse = await axios.post(`${API}/workflow/interactive/${ticketNumber}/start`);
      
      if (interactiveResponse.data.status === 'design_already_completed' || interactiveResponse.data.skip_to_workflow) {
        // Design already done, skip to full workflow
        await axios.post(`${API}/agents/${ticketNumber}/workflow`);
        setTimeout(fetchTicketData, 2000);
        setExecuting(false);
        return;
      }
      
      if (interactiveResponse.data.status === 'waiting_user') {
        // Agent has questions!
        const questions = interactiveResponse.data.questions || [];
        
        if (questions.length > 0) {
          // Show only FIRST question for now (simplified UX)
          setCurrentQuestion(questions[0]);
          setPendingQuestions(questions);
          setQuestionDialogOpen(true);
        }
        setExecuting(false);
        return;
      }
      
      // No questions - continue with full workflow
      await axios.post(`${API}/agents/${ticketNumber}/workflow`);
      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error executing workflow:', error);
      // Fallback to regular workflow if interactive not available
      try {
        const ticketNumber = ticket?.ticket_number || id;
        await axios.post(`${API}/agents/${ticketNumber}/workflow`);
        setTimeout(fetchTicketData, 2000);
      } catch (fallbackError) {
        console.error('Error in fallback workflow:', fallbackError);
      }
    } finally {
      setExecuting(false);
    }
  };

  const executeErrorResolution = async () => {
    if (!errorResolutionPlan) {
      alert('Please wait for error analysis to complete');
      return;
    }
    
    setExecutingResolution(true);
    try {
      const ticketNumber = ticket?.ticket_number || id;
      const response = await axios.post(`${API}/tickets/${ticketNumber}/resolve-error`);
      
      // Add resolution activity to stream
      setActivityStream(prev => [...prev, {
        id: `error-resolution-${Date.now()}`,
        type: 'error_resolution',
        icon: '🔧',
        title: 'Error Resolution Agent Executed',
        message: `Executed ${response.data.execution_results?.length || 0} resolution steps`,
        timestamp: new Date().toISOString(),
        color: 'bg-red-500',
        output: response.data
      }]);
      
      // Show result
      const verification = response.data.verification;
      if (verification?.fixed) {
        alert(`✅ Error Resolved!\n\nConfidence: ${(verification.confidence * 100).toFixed(0)}%\nSteps executed: ${verification.successful_steps}/${verification.total_steps}\n\n${verification.reasoning}`);
      } else {
        alert(`⚠️ Error Resolution Attempted\n\nStatus: ${response.data.final_status}\nSteps executed: ${verification?.successful_steps}/${verification?.total_steps}\n\nPlease review execution results in activity stream.`);
      }
      
      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error executing resolution:', error);
      alert('Error during resolution: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExecutingResolution(false);
    }
  };

  const analyzeError = async () => {
    if (!ticket?.is_error_ticket) return;
    
    try {
      const response = await axios.post(`${API}/errors/resolve`, {
        ticket_id: id,
        error_code: ticket.error_code || 'UNKNOWN',
        error_message: ticket.error_message || ticket.description,
        environment: 'dev',
        context: {
          ticket_number: ticket.ticket_number,
          sap_system: ticket.sap_system,
          error_module: ticket.error_module
        }
      });
      
      setErrorResolutionPlan(response.data);
      
      // Add analysis to activity stream
      setActivityStream(prev => [...prev, {
        id: `error-analysis-${Date.now()}`,
        type: 'error_analysis',
        icon: '🔍',
        title: 'Error Analysis Completed',
        message: `${response.data.source === 'learned_knowledge' ? '🎓 Found in knowledge base' : '🤖 AI generated new resolution'} (${(response.data.confidence * 100).toFixed(0)}% confidence)`,
        timestamp: new Date().toISOString(),
        color: 'bg-blue-500',
        output: response.data
      }]);
    } catch (error) {
      console.error('Error analyzing:', error);
    }
  };

  const handleAnswerQuestion = async () => {
    if (!userAnswer.trim()) {
      alert('Please provide an answer');
      return;
    }
    
    if (!currentQuestion || !currentQuestion.id) {
      alert('Error: Question ID not found. Please try again.');
      return;
    }
    
    try {
      const payload = {
        question_id: currentQuestion.id,
        answer: userAnswer,
        answered_by: "User"
      };
      
      console.log('Submitting answer:', payload);
      
      const response = await axios.post(`${API}/workflow/interactive/${id}/answer`, payload);
      
      setUserAnswer('');
      setQuestionDialogOpen(false);
      setPendingQuestions([]);
      
      // Check response and continue workflow
      if (response.data.status === 'design_completed' || response.data.continue_workflow) {
        // Design complete! Show message and continue with full workflow
        setTimeout(async () => {
          try {
            setExecuting(true);
            alert('Design completed with your input! Continuing with DEV → TEST → DEVOPS → GOVERNANCE agents...');
            const ticketNumber = ticket?.ticket_number || id;
            await axios.post(`${API}/agents/${ticketNumber}/workflow`);
            setTimeout(() => {
              fetchTicketData();
              setExecuting(false);
            }, 2000);
          } catch (err) {
            console.error('Error continuing workflow:', err);
            setExecuting(false);
          }
        }, 1000);
      } else {
        alert('Answer received. Please restart workflow to continue.');
      }
    } catch (error) {
      console.error('Error answering question:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert('Error submitting answer: ' + errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <div className="text-lg">Loading ticket details...</div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Ticket not found</div></div>;
  }

  const unassignedAgents = customAgents.filter(
    ca => !assignedAgents.some(aa => aa.agent_id === ca.id)
  );

  const workflowProgress = (agentTasks.filter(t => t.status === 'completed').length / 5) * 100;

  const _ticketTypeLower = (ticket?.customization_type || ticket?.category || '').toLowerCase();
  const isCodeReport = _ticketTypeLower === 'code report';
  const isSecurity = isSecurityTicketType(ticket);
  const securityAgentMessage = isSecurity
    ? resolveSecurityAgentMessage(ticket?.chain_of_thought, currentCode)
    : '';
  const securityHasMessage = !!(securityAgentMessage && securityAgentMessage.trim().length > 0);
  const securityRunComplete =
    isSecurity && isSecurityAgentComplete(ticket?.chain_of_thought, securityAgentMessage);
  const securityRunFailed =
    isSecurity && isSecurityAgentFailed(ticket?.chain_of_thought, securityAgentMessage);
  const securityRunRunning =
    isSecurity &&
    isSecurityAgentRunning(ticket?.chain_of_thought) &&
    !securityRunComplete &&
    !securityRunFailed;

  return (
    <div className="h-full flex flex-col" data-testid="enhanced-ticket-detail">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-slate-900 dark:border-slate-700">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
              <Badge className="text-xs">{ticket.status.replace('_', ' ')}</Badge>
              {ticket.target_system_name ? (
                <Badge variant="default" className="text-xs bg-blue-600">
                  🎯 {ticket.target_system_name}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">{ticket.sap_system}</Badge>
              )}
            </div>
            <h1 className="text-xl font-bold">{ticket.title}</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <PlatformConnectionStatus connected={ticketPlatformConnected} scope="ticket" />
          <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Users className="mr-2 h-4 w-4" />
                Agents ({assignedAgents.length})
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Custom Agents</DialogTitle>
                <DialogDescription>Select agents to work on this ticket</DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-96">
                <div className="space-y-2">
                  {unassignedAgents.length > 0 ? (
                    unassignedAgents.map(agent => (
                      <Card key={agent.id} className="cursor-pointer hover:bg-accent" onClick={() => assignAgent(agent.id)}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{agent.name}</div>
                              <div className="text-xs text-muted-foreground">{agent.description}</div>
                            </div>
                            <Button size="sm">Assign</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                      {customAgents.length === 0 ? 'No custom agents created yet. Visit Agent Studio to create one.' : 'All agents assigned'}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {!vibeSession && assignedAgents.length > 0 && (
            <Button onClick={startVibeSession} size="sm" disabled={executing}>
              {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Start Vibe
            </Button>
          )}
          
          {ticket.is_error_ticket ? (
            <>
              {!errorResolutionPlan ? (
                <Button size="sm" disabled className="bg-blue-600">
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing Error...
                </Button>
              ) : (
                <span title={permissionDeniedTitle(canUpdateTicket, 'execute resolution')}>
                  <Button onClick={executeErrorResolution} disabled={!canUpdateTicket || executingResolution} size="sm" className="bg-red-600 hover:bg-red-700">
                    {executingResolution ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Executing Steps...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Execute Resolution
                      </>
                    )}
                  </Button>
                </span>
              )}
            </>
          ) : (
            <span title={permissionDeniedTitle(canUpdateTicket, 'run ticket workflow')}>
              <Button onClick={executeFullWorkflow} disabled={!canUpdateTicket || executing} size="sm">
                {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Workflow
              </Button>
            </span>
          )}

          {isCodeReport && (
            <>
              <DeployActionButton
                deployStatus={deployStatus}
                deploying={deploying}
                hasCode={!!(currentCode && !currentCode.trim().startsWith('// Code will appear here'))}
                onClick={openDeployDialog}
                testId="deploy-abap-button"
                workflowStage={ticket?.abap_workflow_stage}
                canDeploy={canUpdateTicket}
              />
              <DeployAndAtcActionButton
                deploying={deploying}
                hasCode={!!(currentCode && !currentCode.trim().startsWith('// Code will appear here'))}
                onClick={openDeployAndAtcDialog}
                testId="deploy-and-run-atc-button"
                canDeploy={canUpdateTicket}
              />
            </>
          )}

          <Button
            onClick={() => setShowFioriPreview(!showFioriPreview)}
            variant={showFioriPreview ? "default" : "outline"}
            size="sm"
          >
            <Eye className="mr-2 h-4 w-4" />
            {showFioriPreview ? 'Hide' : 'Show'} Preview
          </Button>
        </div>
      </div>

      {/* Workflow Progress */}
      {agentTasks.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 border-b">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">Workflow Progress:</span>
            <Progress value={workflowProgress} className="flex-1 h-2" />
            <span className="text-xs text-muted-foreground">{Math.round(workflowProgress)}%</span>
          </div>
        </div>
      )}

      {/* Main Content - Activity Stream Focus */}
      <div className="flex-1 flex overflow-hidden min-w-0">
        {/* PRIMARY: Activity Stream */}
        <div className={`flex flex-col border-r transition-all ${showFioriPreview ? 'flex-1' : 'w-3/5'} min-w-0 overflow-hidden`}>
          <div className="p-3 border-b dark:border-slate-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                <h2 className="font-semibold dark:text-slate-100">Live Agent Activity Stream</h2>
              </div>
              {assignedAgents.length > 0 && (
                <div className="flex gap-1">
                  {assignedAgents.map(a => (
                    <Badge key={a.id} variant="secondary" className="text-xs">
                      {a.agent_name}
                      {a.status === 'assigned' && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-4 w-4 p-0 ml-1"
                          onClick={() => executeAgent(a.agent_id)}
                        >
                          <Play className="h-2 w-2" />
                        </Button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <ScrollArea className="flex-1 p-4 min-w-0">
            <div className="space-y-3 min-w-0">
              {/* Thinking Indicator - Always on top */}
              {(activityStream.length > 0 || isSecurity) && (
                <ThinkingIndicator
                  hasCode={
                    isSecurity
                      ? securityHasMessage
                      : !!(currentCode && currentCode.trim().length > 0)
                  }
                  isCodeReport={isCodeReport}
                  isSecurity={isSecurity}
                  securityRunComplete={securityRunComplete}
                  securityRunFailed={securityRunFailed}
                  securityRunRunning={securityRunRunning}
                  workflowStage={ticket?.abap_workflow_stage}
                  validationStatus={resolveValidationStatus(ticket?.validation_status, currentCode, ticket?.abap_workflow_stage)}
                  deployStatus={deployStatus}
                  deployedObjectName={deployResult?.objectName || ticket?.deployed_object_name}
                  deployMessage={deployResult?.message}
                  deployResponse={deployResult}
                  chainOfThought={ticket?.chain_of_thought || null}
                />
              )}
              
              {/* Chain of Thought */}
              <ChainOfThought chainOfThought={ticket?.chain_of_thought || null} />
              
              {/* Error Resolution Plan (for error tickets) */}
              {ticket.is_error_ticket && errorResolutionPlan && (
                <ErrorResolutionPlan 
                  plan={errorResolutionPlan}
                  onExecute={executeErrorResolution}
                  executing={executingResolution}
                  canExecute={canUpdateTicket}
                />
              )}
              
              {activityStream.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    {ticket.is_error_ticket 
                      ? 'Error analysis in progress... Resolution plan will appear above.'
                      : 'Waiting for agent activity... Execute workflow or assign custom agents to begin.'}
                  </AlertDescription>
                </Alert>
              ) : (
                activityStream
                  .filter(activity => activity.type !== 'code_generated') // Filter out Code generated activities
                  .map((activity) => (
                  <div key={activity.id} className="flex gap-3 animate-in fade-in slide-in-from-left-2 min-w-0">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${activity.color} flex items-center justify-center text-white text-sm shadow-md`}>
                      {activity.icon}
                    </div>
                    <div className="flex-1 bg-white dark:bg-slate-900 border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow min-w-0">
                      <div className="flex items-start justify-between mb-1 min-w-0">
                        <div className="font-medium text-sm break-words min-w-0">{activity.title}</div>
                        <div className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                          {new Date(activity.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground break-words whitespace-pre-wrap min-w-0" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{activity.message}</div>
                      
                      {/* Agent-specific details */}
                      <AgentActivityDetails activity={activity} />
                      
                      {activity.fullMessage && activity.fullMessage.length > 100 && (
                        <details className="mt-2">
                          <summary className="text-xs text-blue-600 cursor-pointer">Show full message</summary>
                          <div className="mt-2 text-xs bg-slate-50 dark:bg-slate-800 p-2 rounded whitespace-pre-wrap">{activity.fullMessage}</div>
                        </details>
                      )}
                      {activity.output?.code && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2"
                          onClick={() => setCurrentCode(extractCodeFromBackticks(activity.output.code))}
                        >
                          <Code2 className="mr-1 h-3 w-3" />
                          View Code
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
              
              <div ref={activityEndRef} />
            </div>
          </ScrollArea>

          {/* Vibe Chat Input */}
          {vibeSession && (
            <div className="p-3 border-t bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex gap-2">
                <Textarea
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendVibeMessage();
                    }
                  }}
                  placeholder="Chat with your assigned agents... (Press Enter to send)"
                  rows={2}
                  className="flex-1 text-sm"
                />
                <Button onClick={sendVibeMessage} disabled={!userMessage.trim() || sendingMessage} size="icon">
                  {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {assignedAgents.length > 0 && (
                <div className="text-xs text-muted-foreground mt-2">
                  Active agents: {assignedAgents.map(a => a.agent_name).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECONDARY: Code & Chat View */}
        <div className={`flex flex-col border-r transition-all ${showFioriPreview ? 'w-96' : 'flex-1'} min-w-0 overflow-hidden`} style={{ maxWidth: '100%' }}>
          {(() => {
            // Determine ticket type from customization_type (Type field) or category (legacy)
            // Prioritize customization_type as it's the main Type field
            const ticketType = ticket?.customization_type || ticket?.category || '';
            
            // Check ticket type (case-insensitive for robustness)
            const ticketTypeLower = ticketType.toLowerCase();
            const isCodeReport = ticketTypeLower === 'code report';
            const isStaticReport = ticketTypeLower === 'static report';
            const isSecurity = ticketTypeLower === 'security';
            
            // Determine label and icon based on Type field
            const codeTabLabel = isStaticReport ? 'Report' : (isSecurity ? 'Agent Message' : 'Code');
            const codeTabIcon = isStaticReport ? <FileText className="h-4 w-4" /> : (isSecurity ? <MessageSquare className="h-4 w-4" /> : <Code2 className="h-4 w-4" />);

            return (
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden" key={`content-${ticket?.id}-${ticketType}`}>
                {/* Styled Header */}
                <div className="m-2 p-3 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-white dark:bg-slate-900 rounded-md shadow-sm border border-slate-200 dark:border-slate-700">
                      {codeTabIcon}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{codeTabLabel}</div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        {isCodeReport ? 'Code editor and generation' : (isStaticReport ? 'Static report access' : 'Security agent analysis')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Code Content - Show code editor for Code Report or default */}
                {(isCodeReport || (!isStaticReport && !isSecurity)) && (
                  <div className="flex-1 m-0 p-2 min-w-0 overflow-hidden">
                    <div className="h-full border rounded-lg overflow-hidden">
                      <Editor
                        key={`editor-${id}`}
                        height="100%"
                        language="javascript"
                        value={currentCode || '// Code will appear here as agents generate it...\n// Execute workflow to start agent collaboration'}
                        onChange={(value) => setCurrentCode(value || '')}
                        theme="vs-dark"
                        options={{
                          minimap: { enabled: false },
                          fontSize: 12,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          wordWrap: 'on',
                          automaticLayout: true,
                          // Disable error indicators
                          renderValidationDecorations: 'off',
                          quickSuggestions: false,
                          suggestOnTriggerCharacters: false,
                          acceptSuggestionOnEnter: 'off',
                          tabCompletion: 'off',
                          wordBasedSuggestions: false
                        }}
                        onMount={(editor, monaco) => {
                          // Disable all diagnostics/error indicators
                          monaco.editor.setModelMarkers(editor.getModel(), 'owner', []);
                          
                          // Disable language service diagnostics
                          const model = editor.getModel();
                          if (model) {
                            monaco.editor.setModelMarkers(model, 'javascript', []);
                            monaco.editor.setModelMarkers(model, 'typescript', []);
                          }
                          
                          // Suppress Monaco Editor cleanup errors
                          const originalError = console.error;
                          console.error = (...args) => {
                            if (args[0]?.toString().includes('Canceled') || 
                                args[0]?.toString().includes('monaco')) {
                              return;
                            }
                            originalError(...args);
                          };
                          
                          // Listen for model changes and clear markers
                          model?.onDidChangeContent(() => {
                            monaco.editor.setModelMarkers(model, 'owner', []);
                            monaco.editor.setModelMarkers(model, 'javascript', []);
                            monaco.editor.setModelMarkers(model, 'typescript', []);
                          });
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Report Content - Show link for Static Report */}
                {isStaticReport && (
                  <div className="flex-1 m-0 p-4 overflow-auto min-w-0">
                    <div className="h-full flex items-center justify-center">
                      <Card className="w-full max-w-md">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Report Link
                          </CardTitle>
                          <CardDescription>
                            Access the static report using the link below
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {currentCode ? (
                            <div className="space-y-4">
                              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                                <div className="text-sm text-muted-foreground mb-2">Report URL:</div>
                                <div className="flex items-center gap-2 text-blue-600 break-all">
                                  <FileText className="h-4 w-4 flex-shrink-0" />
                                  <span className="break-all">{currentCode}</span>
                                </div>
                              </div>
                              <Button
                                onClick={() => {
                                  if (ticket?.id) {
                                    navigate(`/reports?ticketId=${ticket.id}`);
                                  }
                                }}
                                className="w-full"
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                View Report
                              </Button>
                              <Button
                                onClick={() => window.open(currentCode, '_blank')}
                                variant="outline"
                                className="w-full"
                              >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Open External Link
                              </Button>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-muted-foreground">
                              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                              <p>No report link available yet</p>
                              <p className="text-xs mt-2">The report link will appear here once generated</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Agent Message Content - Show message for Security */}
                {isSecurity && (
                  <div className="flex-1 m-0 p-4 overflow-auto min-w-0">
                    <div className="h-full w-full min-w-0">
                      <Card className="h-full w-full min-w-0">
                        <CardHeader className="w-full min-w-0">
                          <CardTitle className="flex items-center gap-2">
                            <MessageSquare className="h-5 w-5 flex-shrink-0" />
                            Agent Message
                          </CardTitle>
                          <CardDescription className="break-words">
                            Security agent analysis and recommendations
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="overflow-auto w-full min-w-0">
                          {securityHasMessage ? (
                            <div className="space-y-4 w-full min-w-0">
                              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border w-full min-w-0">
                                <div className="text-sm font-semibold mb-2 break-words">Message:</div>
                                <div 
                                  className="text-sm break-words whitespace-pre-wrap w-full min-w-0"
                                  style={{
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    wordWrap: 'break-word',
                                    maxWidth: '100%',
                                    minWidth: 0,
                                    boxSizing: 'border-box'
                                  }}
                                >
                                  {securityAgentMessage}
                                </div>
                              </div>
                              
                              {/* Attachments Section - Only for SapAuth agent (Security ticket type) */}
                              {(() => {
                                const isSapAuth = isSapAuthAgentTicket(ticket);
                                const screenshotUrls = ticket?.description ? extractScreenshotUrls(ticket.description) : [];
                                
                                if (isSapAuth && screenshotUrls.length > 0) {
                                  return (
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 w-full min-w-0">
                                      <div className="flex items-center gap-2 mb-3">
                                        <Paperclip className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                        <div className="text-sm font-semibold text-blue-900">Attachments</div>
                                        <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                                          {screenshotUrls.length} {screenshotUrls.length === 1 ? 'image' : 'images'}
                                        </Badge>
                                      </div>
                                      <div className="space-y-4">
                                        {screenshotUrls.map((url, index) => (
                                          <div key={index} className="border rounded-lg overflow-hidden bg-white shadow-sm">
                                            <div className="relative bg-slate-100 flex items-center justify-center min-h-[300px]">
                                              <img
                                                src={url}
                                                alt={`Screenshot ${index + 1}`}
                                                className="max-w-full max-h-[600px] w-auto h-auto object-contain"
                                                onClick={() => window.open(url, '_blank')}
                                                style={{ cursor: 'pointer' }}
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                  e.target.nextSibling.style.display = 'flex';
                                                }}
                                              />
                                              <div className="hidden absolute inset-0 items-center justify-center bg-slate-50 text-slate-400">
                                                <div className="text-center p-4">
                                                  <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                  <p className="text-xs">Image failed to load</p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-muted-foreground">
                              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                              <p>No agent message available yet</p>
                              <p className="text-xs mt-2">The security agent message will appear here once generated</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* TERTIARY: Fiori Preview (Toggle-able) */}
        {showFioriPreview && (
          <div className="w-1/3 min-w-[400px] max-w-[600px]">
            <FioriPreview code={currentCode} ticket={ticket} isVisible={showFioriPreview} />
          </div>
        )}
      </div>

      {/* Agent Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              DESIGN Agent Needs More Information
            </DialogTitle>
            <DialogDescription>
              The agent is analyzing your requirements and needs clarification to create the best design
            </DialogDescription>
          </DialogHeader>
          
          {currentQuestion && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="font-semibold mb-2">{currentQuestion.question}</div>
                {currentQuestion.context && (
                  <div className="text-sm text-muted-foreground">{currentQuestion.context}</div>
                )}
              </div>

              {currentQuestion.options && currentQuestion.options.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium">Select an option:</div>
                  {currentQuestion.options.map((option, idx) => (
                    <Button
                      key={idx}
                      variant={userAnswer === option ? "default" : "outline"}
                      className="w-full justify-start text-left"
                      onClick={() => setUserAnswer(option)}
                    >
                      {option}
                    </Button>
                  ))}
                </div>
              ) : (
                <div>
                  <Label>Your Answer</Label>
                  <Textarea
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Provide your answer here..."
                    rows={4}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleAnswerQuestion} disabled={!userAnswer.trim()} className="flex-1">
                  <Send className="mr-2 h-4 w-4" />
                  Submit Answer & Continue
                </Button>
                <Button variant="outline" onClick={() => setQuestionDialogOpen(false)}>
                  Skip
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ABAP Deploy Dialog */}
      <Dialog open={deployDialogOpen} onOpenChange={setDeployDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {deployDialogMode === 'deploy-and-atc' ? (
                <><TestTube className="h-5 w-5" />Deploy &amp; Run ATC</>
              ) : (
                <><Upload className="h-5 w-5" />Deploy ABAP to SAP</>
              )}
            </DialogTitle>
            <DialogDescription>
              {deployDialogMode === 'deploy-and-atc'
                ? 'Deploy to SAP and auto-remediate ATC P1 findings (syntax re-check between attempts). Progress appears in Chain of Thought.'
                : 'Deploy the code shown in the editor to SAP via the Halo Master Agent.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="deploy-object-name">Object Name</Label>
              <Input
                id="deploy-object-name"
                value={deployObjectName}
                onChange={(e) => setDeployObjectName(e.target.value)}
                placeholder="Z_HELLO_WORLD"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-description">Description</Label>
              <Input
                id="deploy-description"
                value={deployDescription}
                onChange={(e) => setDeployDescription(e.target.value)}
                placeholder="Hello World demo"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeployDialogOpen(false)} disabled={deploying}>Cancel</Button>
            <span title={permissionDeniedTitle(canUpdateTicket, 'deploy ticket code')}>
              <Button
                onClick={deployDialogMode === 'deploy-and-atc' ? handleDeployAndRunAtc : handleDeploy}
                disabled={!canUpdateTicket || deploying || !deployObjectName.trim()}
              >
                {deploying ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {deployDialogMode === 'deploy-and-atc' ? 'Remediating ATC…' : 'Deploying…'}
                  </>
                ) : (
                  deployDialogMode === 'deploy-and-atc' ? 'Deploy & Run ATC' : 'Save & Deploy'
                )}
              </Button>
            </span>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
